import { Splide, SplideSlide, SplideTrack } from '@splidejs/react-splide';
import '@splidejs/react-splide/dist/css/splide.min.css';
import omit from 'lodash/omit';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { useGetShowroomPrContactsQuery } from 'generated/graphql';

import { useGetCurrentShowroomId, useIsShowroomMobileVersion } from 'lib/showroom';

import { Typography } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBackIosRounded';
import ArrowForward from '@material-ui/icons/ArrowForwardIosRounded';

import { refCaller } from 'components/utils';

import ShowroomPRContact from './ShowroomContact/ShowroomPRContact';
import { useStyles } from './styles';

const ShowroomPRContacts = () => {
	const { t } = useTranslation();
	const classes = useStyles();
	const showroomId = useGetCurrentShowroomId();

	const isMobile = useIsShowroomMobileVersion();

	const [screenWidth, setScreenWidth] = React.useState<number>(window.innerWidth);

	React.useEffect(() => {
		const resize = () => setScreenWidth(window.innerWidth);
		window.addEventListener('resize', resize);
		return () => {
			window.removeEventListener('resize', resize);
		};
	}, []);

	const {
		data: {
			getShowroom: { prContacts }
		}
	} = useGetShowroomPrContactsQuery({ variables: { showroomId }, fetchPolicy: 'cache-only' });

	const cleanPrContacts = React.useMemo(
		() => prContacts.filter(prContact => !Object.values(omit(prContact, 'id', '__typename')).every(value => !value)),
		[prContacts]
	);

	const slideRef = React.useRef<Splide>(null);

	const handlePrev = React.useCallback(() => {
		const ref = refCaller<Splide>(slideRef);
		ref.splide.go('<');
	}, []);

	const handleNext = React.useCallback(() => {
		const ref = refCaller<Splide>(slideRef);
		ref.splide.go('>');
	}, []);

	const showButtons = React.useMemo(() => {
		if (cleanPrContacts.length === 1) return false;
		const ref = refCaller<Splide>(slideRef);
		if (!ref) return false;
		return ref.splideRef.current.getElementsByTagName('ul')[0].scrollWidth + 80 > screenWidth;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cleanPrContacts, screenWidth, isMobile, slideRef.current]);

	return (
		<div className={classes.sectionContainer}>
			<div className={classes.titleContainer}>
				<Typography component="h5" className={classes.title}>
					{t('showroom.contact.title')}
				</Typography>
				{showButtons && (
					<div className={classes.buttonContainer}>
						<button onClick={handlePrev} className={classes.navButton}>
							<ArrowBack fontSize={'small'} />
						</button>
						<button onClick={handleNext} className={classes.navButton}>
							<ArrowForward fontSize={'small'} />
						</button>
					</div>
				)}
			</div>
			<div className={classes.contactContainer}>
				<Splide
					className={classes.slider}
					hasTrack={false}
					options={{
						perPage: isMobile ? 1 : 2,
						gap: 16,
						arrows: false,
						pagination: false,
						autoWidth: true,
						drag: isMobile ? true : false,
						autoHeight: true
					}}
					ref={slideRef}
				>
					<SplideTrack>
						{cleanPrContacts.map(prContact => (
							<SplideSlide key={prContact.id}>
								<ShowroomPRContact key={prContact.id} contact={prContact} />
							</SplideSlide>
						))}
					</SplideTrack>
				</Splide>
			</div>
		</div>
	);
};

export default React.memo(ShowroomPRContacts);
